import { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Card,
  Flex,
  HStack,
  InfoBox,
  Spinner,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useSearchParams } from "react-router-dom";
import {
  technicalDocsService,
  TechnicalDocSummary,
} from "services/technicalDocsService";
import { TechnicalDocModal } from "./TechnicalDocModal";

export const TechnicalDocsTab = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [docs, setDocs] = useState<TechnicalDocSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDocId, setOpenDocId] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    technicalDocsService
      .list()
      .then((list) => setDocs(list))
      .catch(() => setError(t("app.help.technicalDocs.loadError")))
      .finally(() => setIsLoading(false));
  }, [t]);

  // Deep-link support: /admin/help?tab=technical-docs&doc=<id> opens that doc
  // directly, and an optional &section=<heading-slug> scrolls straight to the
  // part that matters (e.g. the provider a setup link was pressed from).
  useEffect(() => {
    const docParam = searchParams.get("doc");
    if (docParam) {
      setOpenDocId(docParam);
      setOpenSection(searchParams.get("section"));
    }
  }, [searchParams]);

  return (
    <Box mt={4}>
      <Text color="gray.500" fontSize="sm" mb={4}>
        {t("app.help.technicalDocs.intro")}
      </Text>

      {isLoading && (
        <Flex justify="center" py={10}>
          <Spinner />
        </Flex>
      )}

      {error && !isLoading && <InfoBox variant="error">{error}</InfoBox>}

      {!isLoading && !error && docs.length === 0 && (
        <InfoBox variant="info">{t("app.help.technicalDocs.empty")}</InfoBox>
      )}

      {!isLoading && !error && docs.length > 0 && (
        <VStack align="stretch" gap={3}>
          {docs.map((doc) => (
            <Card.Root
              key={doc.id}
              onClick={() => {
                setOpenDocId(doc.id);
                setOpenSection(null);
              }}
              cursor="pointer"
              transition="all 0.15s ease"
              _hover={{
                borderColor: "var(--accent-info-rule)",
                shadow: "sm",
              }}
            >
              <Card.Body py={3}>
                <HStack gap={3} align="start">
                  <Badge colorScheme="brand" flexShrink={0}>
                    {doc.lastUpdated}
                  </Badge>
                  <Box flex={1}>
                    <Text fontWeight="semibold" fontSize="sm">
                      {doc.title}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {doc.summary}
                    </Text>
                  </Box>
                  <Text
                    fontSize="sm"
                    color="blue.500"
                    flexShrink={0}
                    aria-hidden
                  >
                    {t("app.help.technicalDocs.viewButton")} →
                  </Text>
                </HStack>
              </Card.Body>
            </Card.Root>
          ))}
        </VStack>
      )}

      <TechnicalDocModal
        docId={openDocId}
        isOpen={openDocId !== null}
        section={openSection}
        onClose={() => {
          setOpenDocId(null);
          setOpenSection(null);
        }}
      />
    </Box>
  );
};
